/* cz-motion.js — drives the motion layer in cz-motion.css (added 2026-09-01)
 *
 * Design constraints, same three as the stylesheet:
 *   1. Motion is a reveal or a response. Nothing loops or auto-plays.
 *   2. Small distances only.
 *   3. Fully off under prefers-reduced-motion; the page must be complete and usable if this
 *      file never loads or throws.
 *
 * The LAST point is why the first statement sets data-cz-motion on <html>: cz-motion.css hides
 * reveal elements only once that attribute exists. If this script 404s, errors, or is blocked,
 * the attribute is never set and every element renders plainly. There is no state in which a
 * customer sees a blank page because an animation did not fire.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Bail out completely when the visitor asked for less motion. The CSS also guards this, but
  // not running the observers at all is cheaper and removes any chance of a transform landing.
  if (reduce) return;

  // No IntersectionObserver (very old browser) => leave everything visible, do nothing.
  if (!('IntersectionObserver' in window)) return;

  root.setAttribute('data-cz-motion', '');
  // Icons and the brand mark draw themselves on after first paint (see cz-motion.css "icons").
  // Two frames, so the dashoffset start value is committed before the transition target lands.
  requestAnimationFrame(function () { requestAnimationFrame(function () {
    root.classList.add('cz-icons-in');
    // once the ring + stone have landed, the stone's load-time delays are dropped so a hover glint releases at once
    window.setTimeout(function () { root.classList.add('cz-mark-done'); }, 1400);
  }); });

  /* ---------------------------------------------------------------- scroll reveal */

  // What gets revealed. Chosen so a reveal always wraps a whole idea (a section heading, a card,
  // a band) — never a fragment of a sentence, which reads as a glitch rather than a design.
  var REVEAL = [
    '.cz-sec-head',
    '.cz-grid > .cz-card',
    '.cz-shelf-track > .cz-card',
    '.cz-iwt-text',
    '.cz-iwt-media',
    '.cz-strip-tile',
    '.cz-trust > li',
    '.cz-fact',
    '.cz-fmark',
    '.cz-std',
    '.cz-tabs-sec .cz-tabs'
  ].join(',');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('cz-in');
      io.unobserve(e.target);
      // drop will-change once the transition has finished
      window.setTimeout(function () { e.target.classList.add('cz-done'); }, 1400);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

  function markReveals(scope) {
    var nodes = (scope || document).querySelectorAll(REVEAL);
    // Stagger restarts per parent, so a 12-card grid reads as three rows of four rather than
    // one 12-step ramp that leaves the last card arriving most of a second late.
    var seen = new Map();
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.hasAttribute('data-cz-reveal')) return;
      // Anything already on screen at load is shown immediately and never animated in: a reveal
      // the visitor did not scroll to is just a flash of missing content.
      var r = el.getBoundingClientRect();
      var onscreenAtLoad = r.top < (window.innerHeight * 0.92);
      el.setAttribute('data-cz-reveal', '');
      var p = el.parentElement || document.body;
      var i = seen.get(p) || 0;
      if (i < 8) el.style.setProperty('--cz-i', String(i));   // cap the ramp at ~440ms
      seen.set(p, i + 1);
      if (onscreenAtLoad) {
        el.classList.add('cz-in', 'cz-done');
      } else {
        io.observe(el);
      }
    });
  }
  markReveals(document);

  // Sections can be re-rendered by the theme editor; re-mark whatever it swaps in.
  document.addEventListener('shopify:section:load', function (e) { markReveals(e.target); });

  /* ---------------------------------------------------------------- moving words
   * Splits the hero headline into words and lets each rise on its own beat.
   *
   * The headline contains markup (`<em>chosen one at a time.</em>`), so this walks TEXT NODES
   * and rebuilds them in place rather than touching innerHTML on the h1 — that keeps the <em>,
   * its italic styling and any link intact. Screen readers get the same text either way, and
   * the h1's accessible name is unchanged because we only insert inline spans.
   */
  var h = document.querySelector('.cz-herofull-h');
  if (h && !h.hasAttribute('data-cz-split')) {
    h.setAttribute('data-cz-split', '');
    var idx = 0;
    var walker = document.createTreeWalker(h, NodeFilter.SHOW_TEXT, null);
    var texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);

    texts.forEach(function (node) {
      var parts = node.nodeValue.split(/(\s+)/);      // keep the whitespace runs
      var frag = document.createDocumentFragment();
      parts.forEach(function (part) {
        if (part === '') return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        var outer = document.createElement('span');
        outer.className = 'cz-word';
        var inner = document.createElement('span');
        inner.textContent = part;
        inner.style.setProperty('--cz-i', String(idx++));
        outer.appendChild(inner);
        frag.appendChild(outer);
      });
      node.parentNode.replaceChild(frag, node);
    });

    // next frame, so the initial (hidden) state is painted before the transition starts
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { h.classList.add('cz-words-in'); });
    });
  }

  /* ---------------------------------------------------------------- header on scroll */
  var lastScrolled = null;
  function onScroll() {
    var s = window.scrollY > 8;
    if (s === lastScrolled) return;
    lastScrolled = s;
    document.body.classList.toggle('cz-scrolled', s);
  }
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------------------------------------------------------------- add-to-bag feedback
   * Delegated, so it survives the cart drawer and any section re-render. This is presentation
   * only — it never intercepts the click, never preventDefaults, and never touches the cart.
   * app.js remains the only thing that talks to /cart.
   */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[name="add"], .cz-add, [data-cz-add]');
    if (!btn) return;
    btn.classList.remove('cz-added');
    void btn.offsetWidth;                 // restart the animation
    btn.classList.add('cz-added');
    window.setTimeout(function () { btn.classList.remove('cz-added'); }, 500);
  }, true);

  // Bump the cart count whenever its text changes, wherever app.js chooses to write it.
  var countEl = document.querySelector('[data-cz-cart-count], .cz-cart-count, #cz-cart-count');
  if (countEl && 'MutationObserver' in window) {
    new MutationObserver(function () {
      countEl.classList.remove('cz-bumped');
      void countEl.offsetWidth;
      countEl.classList.add('cz-bumped');
      window.setTimeout(function () { countEl.classList.remove('cz-bumped'); }, 460);
    }).observe(countEl, { childList: true, characterData: true, subtree: true });
  }
})();
